import './CustomBrandingAccordion.scss';
import React, {useEffect, useRef} from 'react'; // TIFF/PDF A11Y CHANGE: useRef/useEffect manage read-only focus inside Custom Branding without changing reusable components.
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import TagsSection from '../tags-section/TagsSection';
import TextPropertyToolbar from '../text-property-toolbar/TextPropertyToolbar';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TagItem} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {t} from 'i18next';

interface BrandingData {
  brandingText: string;
  brandingLogicId: number;
  selectedTagList: string;
  selectedTags: TagItem[];
  disabledTags?: TagItem[];
  brandingFont: string;
  brandingFontStyle: string;
  brandingFontSize: number;
}

interface CustomBrandingAccordionProps {
  id: string;
  index: number;
  brandingData: BrandingData;
  onDelete: (id: string) => void;
  onBrandingDataChange: (id: string, data: BrandingData) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  fontOptions: ISelectItem[];
  fontStyleOptions: ISelectItem[];
  fontSizeOptions: ISelectItem[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  placeholder?: string;
  disabled?: boolean;
}

const CustomBrandingAccordion: React.FC<CustomBrandingAccordionProps> = ({
  id,
  index,
  brandingData,
  onDelete,
  onBrandingDataChange,
  tagsTreeData = [],
  fontOptions,
  fontStyleOptions,
  fontSizeOptions,
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  placeholder,
  disabled = false,
}) => {
  const customBrandingDetailsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!disabled) {
      return;
    }

    let animationFrameId = 0;

    const getControlText = (element: HTMLElement): string =>
      (element.getAttribute('aria-label') || element.textContent || '')
        .trim()
        .replace(/\s+/g, ' ');

    const setNonFocusable = (element: HTMLElement | null | undefined) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-disabled', 'true');
    };

    const setFocusable = (
      element: HTMLElement | null | undefined,
      ariaLabel?: string
    ) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-disabled', 'true');
      if (ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }
    };

    const updateReadOnlyFocusOrder = () => {
      const detailsElement = customBrandingDetailsRef.current;
      if (!detailsElement) return;

      detailsElement
        .querySelectorAll<HTMLElement>(
          '[tabindex], button, input, select, textarea, a[href], [role="button"], [role="combobox"], [role="listbox"], [role="option"]'
        )
        .forEach(setNonFocusable);

      detailsElement
        .querySelectorAll<HTMLElement>(
          '[data-testid^="filtered-selected-tags-chip-"]'
        )
        .forEach((chipElement) => {
          const chipText = getControlText(chipElement);
          if (chipText) {
            chipElement
              .querySelectorAll<HTMLElement>(
                'button, [role="button"], [tabindex]'
              )
              .forEach((nestedElement) => {
                if (nestedElement !== chipElement) {
                  setNonFocusable(nestedElement);
                }
              });
            setFocusable(chipElement, chipText);
          }
        });
    };

    const scheduleFocusUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(updateReadOnlyFocusOrder);
    };

    scheduleFocusUpdate();

    const observer = new MutationObserver(scheduleFocusUpdate);
    if (customBrandingDetailsRef.current) {
      observer.observe(customBrandingDetailsRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [disabled, brandingData.selectedTags, brandingData.brandingText]); // TIFF/PDF A11Y CHANGE: keeps read-only focus behavior in sync with Custom Branding content.

  const handleTagsChange = (tags: TagItem[]) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      selectedTags: tags,
    });
  };

  const handleFontChange = (font: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingFont: font,
    });
  };

  const handleFontStyleChange = (style: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingFontStyle: style,
    });
  };

  const handleFontSizeChange = (size: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingFontSize: Number(size),
    });
  };

  const handleTextChange = (text: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingText: text,
    });
  };
  return (
    <Box className="custom-branding-accordion-wrapper">
      <Box className="custom-branding-header-wrapper">
        <Accordion className="custom-branding-accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="custom-branding-accordion-summary"
            aria-label={`Custom Branding ${index + 1} collapse toggle`}
          >
            <Typography
              className="typo-text subhead4_size custom-branding-title"
              data-testid={`custom-branding-accordion-custom-branding-accordion-header`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.custom.customBrandingAccordionHeader',
                {index: index + 1}
              )}
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            className="custom-branding-details"
            /* TIFF/PDF A11Y CHANGE: used to manage descendant focus in read-only mode. */
            ref={customBrandingDetailsRef}
            // TIFF/PDF A11Y CHANGE: defensive blur if a child control receives focus programmatically in read-only mode.
          >
            <Box className="custom-branding-content">
              {/* Tags Section */}
              <TagsSection
                selectedTags={brandingData.selectedTags}
                onTagsChange={handleTagsChange}
                tagsTreeData={tagsTreeData}
                disabledTags={brandingData.disabledTags}
                disabled={disabled}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                dataTestId="branding-text"
                fontFamily={brandingData.brandingFont}
                fontStyle={brandingData.brandingFontStyle}
                fontSize={String(brandingData.brandingFontSize)}
                toolbarText={brandingData.brandingText}
                fontOptions={fontOptions}
                fontStyleOptions={fontStyleOptions}
                fontSizeOptions={fontSizeOptions}
                insertFieldTypes={insertFieldTypes}
                metadataFieldOptions={metadataFieldOptions}
                productionFieldOptions={productionFieldOptions}
                onFontFamilyChange={handleFontChange}
                onFontStyleChange={handleFontStyleChange}
                onFontSizeChange={handleFontSizeChange}
                onToolbarTextChange={handleTextChange}
                onInsertField={(field) => {
                  handleTextChange(
                    `${brandingData.brandingText || ''}$${field}$`
                  );
                }}
                placeholder={placeholder}
                disabled={disabled}
                textAreaTestId={`Enter-branding-text-input-${index}`}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        {!disabled && (
          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => onDelete(id)}
            className="delete-btn"
            dataTestId="custom-branding-accordion-button"
          >
            <DeleteIcon />
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default CustomBrandingAccordion;
